import { ColDef, GridOptions } from 'ag-grid-community';

/**
 * Loads AG Grid state from localStorage safely, handling column changes automatically.
 */
const loadAgGridState = (
  gridOptions :GridOptions,
  columnDefs :ColDef[],
  storageKey :string,
) :void => {
  const savedState = localStorage.getItem(storageKey);

  if (!savedState || !gridOptions?.api || !gridOptions?.columnApi) return;

  const { columnState, filterState, savedColumns } = JSON.parse(savedState);

  // Get current column fields (ignore undefined fields)
  const currentColumns = columnDefs.map((col) => col.field).filter(Boolean);

  // If savedColumns is undefined or does not match current columns, reset the grid state
  if (
    !savedColumns ||
    !areCurrentAndSavedColumnDefsEqual(currentColumns, savedColumns)
  ) {
    console.warn('Column definitions have changed. Resetting grid state.');
    localStorage.removeItem(storageKey);

    return;
  }

  // Apply only valid columns from saved state
  const validColumns = gridOptions.columnApi
    .getAllColumns()
    .map((col) => col.getColId());
  const filteredColumnState =
    columnState?.filter((col) => validColumns.includes(col.colId)) || [];

  gridOptions.columnApi.applyColumnState({
    state: filteredColumnState,
    applyOrder: true,
  });
  gridOptions.api.setFilterModel(filterState);
};

/**
 * Saves AG Grid state to localStorage with automatic column tracking.
 */
const saveAgGridState = (
  gridOptions :GridOptions,
  columnDefs :ColDef[],
  storageKey :string,
) :void => {
  if (!gridOptions?.api || !gridOptions?.columnApi) return;

  const currentColumns = columnDefs.map((col) => col.field).filter(Boolean);

  const gridState = {
    columnState: gridOptions.columnApi.getColumnState(),
    filterState: gridOptions.api.getFilterModel(),
    savedColumns: currentColumns.length > 0 ? currentColumns : undefined, // ✅ Ensure it's defined
  };

  localStorage.setItem(storageKey, JSON.stringify(gridState));
};

/**
 * Compares two arrays **ignoring order** while handling undefined cases.
 */
const areCurrentAndSavedColumnDefsEqual = (
  arr1 ?:string[],
  arr2 ?:string[],
) :boolean => {
  if (!arr1 || !arr2) return false; // ✅ Prevents undefined errors
  if (arr1.length !== arr2.length) return false;

  return arr1.slice().sort().join(',') === arr2.slice().sort().join(',');
};

/**
 * Exported utility functions for AG Grid state management.
 */
export const AgGridStateUtils = {
  saveAgGridState,
  loadAgGridState,
};
